/**
 * Hook for managing voice configuration and testing
 * Handles voice loading, settings, testing, and audio recording
 */

import { useState, useCallback, useEffect } from 'react';
import { api } from '../../../utils/api';
import { getApiBaseUrl } from '../../../utils/env';
import toast from 'react-hot-toast';
import { Voice, VoiceCollection, VoiceSettings, VoiceProvider, TranscriptionResult, AudioRecording } from '../types/voice.types';

export interface UseVoiceManagementReturn {
  voices: VoiceCollection;
  selectedVoice: Voice | null;
  voiceSettings: VoiceSettings;
  isLoadingVoices: boolean;
  isTesting: boolean;
  isTestingVoice: boolean;
  loading: boolean;
  activeProvider: VoiceProvider;
  isRecording: boolean;
  recordingDuration: number;
  mediaRecorder: MediaRecorder | null;
  audioStream: MediaStream | null;
  savedVoiceSettings: VoiceSettings | null;
  transcriptionResult: TranscriptionResult | null;
  error: string | null;
  loadVoices: () => Promise<void>;
  loadVoiceSettings: (assistantId: string) => Promise<void>;
  saveVoiceSettings: (assistantId: string) => Promise<boolean>;
  testVoice: (text: string) => Promise<void>;
  selectVoice: (voice: Voice) => void;
  updateVoiceSettings: (updates: Partial<VoiceSettings>) => void;
  setActiveProvider: (provider: VoiceProvider) => void;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<Blob | null>;
  transcribeAudio: (audioBlob: Blob) => Promise<TranscriptionResult | null>;
  cleanup: () => void;
}

export const useVoiceManagement = (): UseVoiceManagementReturn => {
  const [voices, setVoices] = useState<VoiceCollection>({
    openai: [],
    gemini: []
  });
  const [selectedVoice, setSelectedVoice] = useState<Voice | null>(null);
  const [voiceSettings, setVoiceSettings] = useState<VoiceSettings>({
    speed: 1.0,
    pitch: 0,
    language: 'en'
  });
  const [isLoadingVoices, setIsLoadingVoices] = useState(true);
  const [isTesting, setIsTesting] = useState(false);
  const [activeProvider, setActiveProvider] = useState<VoiceProvider>('openai');
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState<number>(0);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [audioStream, setAudioStream] = useState<MediaStream | null>(null);
  const [savedVoiceSettings, setSavedVoiceSettings] = useState<VoiceSettings | null>(null);
  const [transcriptionResult, setTranscriptionResult] = useState<TranscriptionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);

  /**
   * Load available voices from API
   */
  const loadVoices = useCallback(async () => {
    setIsLoadingVoices(true);
    setError(null);

    try {
      const response = await api.get('/voice/voices');

      if (response.data?.success && response.data?.voices) {
        setVoices(response.data.voices);

        // Set default selected voice (first from openai provider)
        if (response.data.voices.openai?.length > 0) {
          setSelectedVoice(response.data.voices.openai[0]);
        }
      } else {
        throw new Error('Invalid voices data received');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to load voices';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Failed to load voices:', err);
    } finally {
      setIsLoadingVoices(false);
    }
  }, []);

  /**
   * Load saved voice settings for assistant
   */
  const loadVoiceSettings = useCallback(async (assistantId: string) => {
    if (!assistantId) {
      return;
    }

    setError(null);

    try {
      const response = await api.get(`/voice/settings/${assistantId}`);

      if (response.data?.success && response.data?.voiceSettings) {
        const settings = response.data.voiceSettings;
        setSavedVoiceSettings(settings);
        setVoiceSettings({
          speed: settings.speed || 1.0,
          pitch: settings.pitch || 0,
          language: settings.language || 'en',
          voiceId: settings.voiceId,
          provider: settings.provider
        });
      }
    } catch (err: any) {
      // Silently fail - settings might not exist yet
      console.log('No voice settings found for assistant:', assistantId);
    }
  }, []);

  /**
   * Save voice settings for assistant
   */
  const saveVoiceSettings = useCallback(async (assistantId: string): Promise<boolean> => {
    if (!assistantId) {
      toast.error('Assistant ID is required');
      return false;
    }

    if (!selectedVoice) {
      toast.error('Please select a voice first');
      return false;
    }

    setError(null);

    try {
      const response = await api.put(`/voice/settings/${assistantId}`, {
        voiceId: selectedVoice.id,
        provider: selectedVoice.provider,
        speed: voiceSettings.speed,
        pitch: voiceSettings.pitch,
        language: voiceSettings.language
      });

      if (response.data?.success) {
        setSavedVoiceSettings(response.data.voiceSettings);
        toast.success('Voice settings saved successfully');
        return true;
      } else {
        throw new Error('Failed to save voice settings');
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to save voice settings';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Failed to save voice settings:', err);
      return false;
    }
  }, [selectedVoice, voiceSettings]);

  /**
   * Test voice playback with sample text
   */
  const testVoice = useCallback(async (text: string) => {
    if (!selectedVoice) {
      toast.error('Please select a voice first');
      return;
    }

    if (!text.trim()) {
      toast.error('Please enter test text');
      return;
    }

    setIsTesting(true);
    setError(null);

    try {
      const response = await api.post('/voice/test-voice', {
        voiceId: selectedVoice.id,
        text: text.trim(),
        speed: voiceSettings.speed,
        pitch: voiceSettings.pitch,
        provider: selectedVoice.provider
      });

      if (response.data?.success) {
        if (response.data.audioUrl) {
          // Play the generated audio
          toast.success(`Playing ${selectedVoice.name} voice...`);

          const baseUrl = getApiBaseUrl().replace('/api', '');
          const audio = new Audio(`${baseUrl}${response.data.audioUrl}`);

          audio.onloadeddata = () => {
            audio.play().catch(err => {
              console.error('Failed to play audio:', err);
              toast.error('Failed to play audio. Please check your browser settings.');
            });
          };

          audio.onended = () => {
            toast.success('Voice test completed');
          };

          audio.onerror = (err) => {
            console.error('Audio playback error:', err);
            toast.error('Failed to load audio file');
          };
        } else if (response.data.audioError) {
          toast.error(response.data.audioError);
        } else {
          toast.error('Audio generation not available');
        }
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Voice test failed';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Voice test failed:', err);
    } finally {
      setIsTesting(false);
    }
  }, [selectedVoice, voiceSettings]);

  /**
   * Select a voice
   */
  const selectVoice = useCallback((voice: Voice) => {
    setSelectedVoice(voice);
    setActiveProvider(voice.provider);
  }, []);

  /**
   * Update voice settings
   */
  const updateVoiceSettings = useCallback((updates: Partial<VoiceSettings>) => {
    setVoiceSettings(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  /**
   * Start audio recording
   */
  const startRecording = useCallback(async () => {
    console.log('🔴 startRecording CALLED');

    try {
      // Clear previous audio chunks
      console.log('🔴 Clearing audio chunks');
      setAudioChunks([]);

      console.log('🔴 Requesting microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      console.log('🔴 Microphone access granted, stream:', stream);
      setAudioStream(stream);

      const recorder = new MediaRecorder(stream);
      console.log('🔴 MediaRecorder created:', recorder);
      const chunks: Blob[] = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
          console.log('🔴 Audio chunk added, total chunks:', chunks.length);
        }
      };

      // Store chunks when recording stops
      recorder.onstop = () => {
        console.log('🔴 MediaRecorder stopped, setting audio chunks:', chunks.length);
        setAudioChunks(chunks);
      };

      console.log('🔴 Starting MediaRecorder...');
      recorder.start();
      console.log('🔴 MediaRecorder started, state:', recorder.state);

      setMediaRecorder(recorder);
      console.log('🔴 Setting isRecording to TRUE');
      setIsRecording(true);
      console.log('🔴 isRecording should now be true');

      toast.success('🎤 Recording started... speak now');
      console.log('🔴 Toast displayed');
    } catch (err: any) {
      console.error('🔴 ERROR in startRecording:', err);
      const errorMessage = err.message || 'Failed to access microphone';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Failed to start recording:', err);
    }
  }, []);

  /**
   * Stop audio recording and return the blob
   */
  const stopRecording = useCallback(async (): Promise<Blob | null> => {
    console.log('🔴 stopRecording CALLED');
    return new Promise((resolve) => {
      if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        console.log('🔴 MediaRecorder is active, stopping...');
        const recorder = mediaRecorder;

        // Store the chunks that will be collected
        const collectedChunks: Blob[] = [];

        // Save the original onstop handler
        const originalOnStop = recorder.onstop;

        // Override onstop to collect chunks and create blob
        recorder.onstop = (event) => {
          console.log('🔴 Recorder onstop fired');

          // Call original handler to trigger ondataavailable one last time
          if (originalOnStop) {
            originalOnStop.call(recorder, event);
          }

          // Wait for final data to be collected
          setTimeout(() => {
            console.log('🔴 Creating blob from collected chunks:', collectedChunks.length);
            if (collectedChunks.length > 0) {
              const blob = new Blob(collectedChunks, { type: 'audio/webm' });
              console.log('🔴 Blob created, size:', blob.size);
              setAudioChunks([]);
              resolve(blob);
            } else {
              console.log('🔴 No chunks collected!');
              resolve(null);
            }
          }, 100);
        };

        // Override ondataavailable to capture chunks in this scope
        recorder.ondataavailable = (event) => {
          console.log('🔴 Data available event, size:', event.data.size);
          if (event.data.size > 0) {
            collectedChunks.push(event.data);
            console.log('🔴 Chunk added to collectedChunks, total:', collectedChunks.length);
          }
        };

        // Stop the recorder (this will trigger one final ondataavailable)
        console.log('🔴 Calling recorder.stop()');
        recorder.stop();

        // Clean up audio stream
        if (audioStream) {
          console.log('🔴 Stopping audio stream tracks');
          audioStream.getTracks().forEach(track => track.stop());
          setAudioStream(null);
        }

        setIsRecording(false);
        setRecordingDuration(0);
        toast.success('Recording stopped');
      } else {
        console.log('🔴 MediaRecorder not active or null');
        resolve(null);
      }
    });
  }, [mediaRecorder, audioStream]);

  /**
   * Transcribe audio to text using Whisper API
   */
  const transcribeAudio = useCallback(async (audioBlob: Blob): Promise<TranscriptionResult | null> => {
    console.log('🔴 transcribeAudio CALLED with blob size:', audioBlob.size);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      console.log('🔴 Sending transcription request...');
      const response = await api.post('/voice/transcribe', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      console.log('🔴 Transcription response:', response.data);

      if (response.data?.success) {
        // API returns 'transcription' field, not 'text'
        const transcriptionText = response.data.transcription || response.data.text;

        if (transcriptionText) {
          const result: TranscriptionResult = {
            text: transcriptionText,
            confidence: response.data.confidence,
            language: response.data.language,
            duration: response.data.duration
          };

          console.log('🔴 Setting transcription result:', result);
          setTranscriptionResult(result);
          toast.success('Transcription complete');
          return result;
        }
      }

      throw new Error('Invalid transcription response');
    } catch (err: any) {
      console.error('🔴 Transcription error:', err);
      const errorMessage = err.response?.data?.error || err.message || 'Failed to transcribe audio';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Transcription failed:', err);
      return null;
    }
  }, []);

  /**
   * Cleanup audio streams and recorders
   */
  const cleanup = useCallback(() => {
    console.log('🔴 CLEANUP CALLED - stopping recording');
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      console.log('🔴 Stopping mediaRecorder in cleanup');
      mediaRecorder.stop();
    }
    if (audioStream) {
      console.log('🔴 Stopping audioStream tracks in cleanup');
      audioStream.getTracks().forEach(track => track.stop());
    }
    setAudioStream(null);
    setMediaRecorder(null);
    setIsRecording(false);
  }, [mediaRecorder, audioStream]);

  /**
   * Track recording duration
   */
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (isRecording) {
      console.log('🔴 Starting duration counter');
      interval = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } else {
      console.log('🔴 Stopping duration counter');
      if (interval) {
        clearInterval(interval);
      }
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isRecording]);

  /**
   * Cleanup on unmount only
   */
  useEffect(() => {
    console.log('🔴 Cleanup effect registered');
    return () => {
      console.log('🔴 Component unmounting, calling cleanup');
      cleanup();
    };
  }, []);

  return {
    voices,
    selectedVoice,
    voiceSettings,
    isLoadingVoices,
    isTesting,
    isTestingVoice: isTesting, // Alias
    loading: isLoadingVoices,  // Alias
    activeProvider,
    isRecording,
    recordingDuration,
    mediaRecorder,
    audioStream,
    savedVoiceSettings,
    transcriptionResult,
    error,
    loadVoices,
    loadVoiceSettings,
    saveVoiceSettings,
    testVoice,
    selectVoice,
    updateVoiceSettings,
    setActiveProvider,
    startRecording,
    stopRecording,
    transcribeAudio,
    cleanup
  };
};
